import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  VStack,
  HStack,
  Input,
  Radio,
  RadioGroup,
  Textarea,
  FormControl,
  FormLabel,
  Heading,
  Image,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";

const ITEM_TYPES = {
  CHECKBOX: "checkbox",
  MULTIPLE_CHOICE: "multipleChoice",
  THREE_OPTION: "threeOption",
  TEXT: "text",
  NUMERIC: "numeric",
  IMAGE: "image",
};

const ChecklistForm = ({ checklist, onSubmit, onCancel }) => {
  const [responses, setResponses] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    // Load existing responses if available
    if (checklist?.responses) {
      setResponses(checklist.responses);
    }
  }, [checklist]);

  const handleCheckboxChange = (itemId, value) => {
    setResponses({ ...responses, [itemId]: value });
  };

  const handleMultipleChoiceChange = (itemId, value) => {
    setResponses({ ...responses, [itemId]: value });
  };

  const handleTextChange = (itemId, value) => {
    setResponses({ ...responses, [itemId]: value });
  };

  const handleNumericChange = (itemId, value) => {
    setResponses({ ...responses, [itemId]: value });
  };

  const handleImageUpload = (itemId, files) => {
    const imageFiles = Array.from(files);
    // Convert files to base64 for storage
    const promises = imageFiles.map((file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve({
            name: file.name,
            size: file.size,
            type: file.type,
            data: reader.result, // base64 string
          });
        };
        reader.readAsDataURL(file);
      });
    });

    Promise.all(promises).then((convertedFiles) => {
      setResponses({
        ...responses,
        [itemId]: convertedFiles,
      });
    });
  };

  const handleSubmit = async () => {
    onSubmit(responses);
  };

  if (!checklist) {
    return <Box p={8}>No checklist loaded</Box>;
  }

  return (
    <Box>
      <Heading size='md' mb={6}>
        {checklist.checklistName}
      </Heading>

      <VStack spacing={6} align='stretch'>
        {checklist.items?.map((item, index) => (
          <FormControl key={index} as='fieldset'>
            <FormLabel fontSize='md' fontWeight='bold'>
              {item.question}
            </FormLabel>

            {item.type === ITEM_TYPES.CHECKBOX && (
              <RadioGroup value={responses[index] || ""} onChange={(value) => handleCheckboxChange(index, value)}>
                <HStack align='start' pl={4} spacing={4}>
                  <Radio value='Yes'>Yes</Radio>
                  <Radio value='No'>No</Radio>
                </HStack>
              </RadioGroup>
            )}

            {item.type === ITEM_TYPES.THREE_OPTION && (
              <RadioGroup value={responses[index] || ""} onChange={(value) => handleMultipleChoiceChange(index, value)}>
                <HStack align='start' pl={4} spacing={4}>
                  <Radio value='Yes'>Yes</Radio>
                  <Radio value='No'>No</Radio>
                  <Radio value='Not Applicable'>Not Applicable</Radio>
                </HStack>
              </RadioGroup>
            )}

            {item.type === ITEM_TYPES.MULTIPLE_CHOICE && (
              <RadioGroup value={responses[index] || ""} onChange={(value) => handleMultipleChoiceChange(index, value)}>
                <VStack align='start' pl={4} spacing={2}>
                  {item.options?.map((option, optIndex) => (
                    <Radio key={optIndex} value={option}>
                      {option}
                    </Radio>
                  ))}
                </VStack>
              </RadioGroup>
            )}

            {item.type === ITEM_TYPES.TEXT && (
              <Textarea
                value={responses[index] || ""}
                onChange={(e) => handleTextChange(index, e.target.value)}
                placeholder='Enter your response'
                mt={2}
              />
            )}

            {item.type === ITEM_TYPES.NUMERIC && (
              <Input
                type='number'
                value={responses[index] || ""}
                onChange={(e) => handleNumericChange(index, e.target.value)}
                placeholder='Enter a number'
                mt={2}
              />
            )}

            {item.type === ITEM_TYPES.IMAGE && (
              <VStack align='stretch' spacing={3} mt={2}>
                <Input
                  pt='1'
                  type='file'
                  accept='image/*'
                  multiple
                  onChange={(e) => handleImageUpload(index, e.target.files)}
                  placeholder='Upload images'
                />
                {responses[index] && responses[index].length > 0 && (
                  <VStack align='stretch' spacing={2} mt={2}>
                    {responses[index].map((file, fileIndex) => (
                      <Box
                        key={fileIndex}
                        p={2}
                        border='1px solid'
                        borderColor='gray.200'
                        borderRadius='md'
                        display='flex'
                        alignItems='center'
                        gap={3}
                      >
                        {file.data && (
                          <Image
                            src={file.data}
                            alt={file.name}
                            boxSize='60px'
                            objectFit='cover'
                            borderRadius='md'
                            mr={2}
                            cursor='pointer'
                            _hover={{ opacity: 0.8 }}
                            onClick={() => {
                              setSelectedImage(file.data);
                              onOpen();
                            }}
                          />
                        )}
                        <Box>
                          <Text fontSize='sm' fontWeight='500'>
                            {file.name}
                          </Text>
                          <Text fontSize='xs' color='gray.500'>
                            {file.size ? `${(file.size / 1024).toFixed(2)} KB` : "Image"}
                          </Text>
                        </Box>
                      </Box>
                    ))}
                  </VStack>
                )}
              </VStack>
            )}
          </FormControl>
        ))}
      </VStack>

      <HStack justify='flex-end' mt={8} mb='32' spacing={3}>
        {onCancel && (
          <Button variant='outline' onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button colorScheme='green' onClick={handleSubmit}>
          Submit Checklist
        </Button>
      </HStack>

      {/* Image Preview Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size='6xl' isCentered>
        <ModalOverlay bg='blackAlpha.800' />
        <ModalContent bg='transparent' boxShadow='none'>
          <ModalCloseButton color='white' size='lg' />
          <ModalBody p={0} display='flex' justifyContent='center' alignItems='center'>
            {selectedImage && (
              <Image src={selectedImage} alt='Preview' maxH='90vh' maxW='100%' objectFit='contain' borderRadius='md' />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ChecklistForm;
